import * as React from "react";
import { useEffect, useState } from "react";
import useApi from "@/hooks/useApi";
import { PrimaryBlueButton } from "@velaro/velaro-component-lib";

interface ChatEmailTheme {
  siteId: number;
  logoUrl?: string;
  fromName?: string;
  primaryColor: string;
  backgroundColor: string;
  textColor: string;
  footerText?: string;
  poweredByEnabled: boolean;
}

interface ChatTranscriptSettings {
  siteId: number;
  enableVisitorTranscript: boolean;
  emailSubject?: string;
  introText?: string;
  enableDeptOverrides: boolean;
}

const defaultTheme: ChatEmailTheme = {
  siteId: 0,
  primaryColor: "#0057B8",
  backgroundColor: "#f4f4f7",
  textColor: "#333333",
  poweredByEnabled: true
};

const defaultSettings: ChatTranscriptSettings = {
  siteId: 0,
  enableVisitorTranscript: true,
  enableDeptOverrides: false
};

export default function TranscriptSettings() {
  const api = useApi();
  const [theme, setTheme] = useState<ChatEmailTheme>(defaultTheme);
  const [settings, setSettings] =
    useState<ChatTranscriptSettings>(defaultSettings);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [activeTab, setActiveTab] = useState<"settings" | "theme">("settings");

  useEffect(() => {
    async function load() {
      try {
        const [themeRes, settingsRes] = await Promise.all([
          api.admin.get("ChatEmailTheme"),
          api.admin.get("TranscriptSettings")
        ]);
        const themeData = await themeRes.json();
        const settingsData = await settingsRes.json();
        setTheme({ ...defaultTheme, ...themeData });
        setSettings({ ...defaultSettings, ...settingsData });
      } catch {
        // safe defaults already set
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  async function save() {
    setSaving(true);
    try {
      await Promise.all([
        api.admin.post("ChatEmailTheme", theme),
        api.admin.post("TranscriptSettings", settings)
      ]);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      // handle error
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="p-6 text-gray-500">Loading...</div>;

  return (
    <div className="max-w-2xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            Transcript Emails
          </h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Configure how chat transcripts are sent to visitors.
          </p>
        </div>
        <PrimaryBlueButton
          onClick={save}
          label={saving ? "Saving..." : saved ? "Saved" : "Save Changes"}
        />
      </div>

      {/* Tab nav */}
      <div className="flex border-b mb-6">
        {(["settings", "theme"] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
              activeTab === tab
                ? "border-blue-600 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            {tab === "settings" ? "Settings" : "Email Theme"}
          </button>
        ))}
      </div>

      {activeTab === "settings" && (
        <div className="space-y-5">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={settings.enableVisitorTranscript}
              onChange={(e) =>
                setSettings((s) => ({
                  ...s,
                  enableVisitorTranscript: e.target.checked
                }))
              }
              className="w-4 h-4 rounded"
            />
            <div>
              <div className="text-sm font-medium text-gray-800">
                Enable &quot;Email me this transcript&quot;
              </div>
              <div className="text-xs text-gray-500">
                Visitors will see an option to email themselves a copy of the
                conversation.
              </div>
            </div>
          </label>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email Subject
            </label>
            <input
              type="text"
              value={settings.emailSubject ?? ""}
              placeholder="Your chat transcript"
              onChange={(e) =>
                setSettings((s) => ({
                  ...s,
                  emailSubject: e.target.value || undefined
                }))
              }
              className="w-full border rounded px-3 py-2 text-sm"
            />
            <p className="text-xs text-gray-500 mt-1">
              Supports <code>{"<<company_name>>"}</code> variable.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Intro Text
            </label>
            <textarea
              value={settings.introText ?? ""}
              placeholder="Thank you for chatting with us! Here's a copy of your conversation."
              rows={3}
              onChange={(e) =>
                setSettings((s) => ({
                  ...s,
                  introText: e.target.value || undefined
                }))
              }
              className="w-full border rounded px-3 py-2 text-sm"
            />
            <p className="text-xs text-gray-500 mt-1">
              Shown above the transcript. Supports{" "}
              <code>{"<<visitor_name>>"}</code>,{" "}
              <code>{"<<agent_name>>"}</code>,{" "}
              <code>{"<<company_name>>"}</code>.
            </p>
          </div>
        </div>
      )}

      {activeTab === "theme" && (
        <div className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Logo URL
            </label>
            <input
              type="url"
              value={theme.logoUrl ?? ""}
              placeholder="https://yourcompany.com/logo.png (leave blank to use site logo)"
              onChange={(e) =>
                setTheme((t) => ({
                  ...t,
                  logoUrl: e.target.value || undefined
                }))
              }
              className="w-full border rounded px-3 py-2 text-sm"
            />
            {theme.logoUrl && (
              <img
                src={theme.logoUrl}
                alt="Email logo preview"
                className="mt-2 h-10 object-contain border rounded p-1 bg-white"
              />
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              From Name
            </label>
            <input
              type="text"
              value={theme.fromName ?? ""}
              placeholder="Leave blank to use your company name"
              onChange={(e) =>
                setTheme((t) => ({
                  ...t,
                  fromName: e.target.value || undefined
                }))
              }
              className="w-full border rounded px-3 py-2 text-sm"
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Brand Color
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={theme.primaryColor}
                  onChange={(e) =>
                    setTheme((t) => ({ ...t, primaryColor: e.target.value }))
                  }
                  className="w-10 h-9 rounded border cursor-pointer"
                />
                <input
                  type="text"
                  value={theme.primaryColor}
                  onChange={(e) =>
                    setTheme((t) => ({ ...t, primaryColor: e.target.value }))
                  }
                  className="border rounded px-2 py-1 text-xs w-24"
                />
              </div>
              <p className="text-xs text-gray-400 mt-1">Header bar + button</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Background
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={theme.backgroundColor}
                  onChange={(e) =>
                    setTheme((t) => ({
                      ...t,
                      backgroundColor: e.target.value
                    }))
                  }
                  className="w-10 h-9 rounded border cursor-pointer"
                />
                <input
                  type="text"
                  value={theme.backgroundColor}
                  onChange={(e) =>
                    setTheme((t) => ({
                      ...t,
                      backgroundColor: e.target.value
                    }))
                  }
                  className="border rounded px-2 py-1 text-xs w-24"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Text Color
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={theme.textColor}
                  onChange={(e) =>
                    setTheme((t) => ({ ...t, textColor: e.target.value }))
                  }
                  className="w-10 h-9 rounded border cursor-pointer"
                />
                <input
                  type="text"
                  value={theme.textColor}
                  onChange={(e) =>
                    setTheme((t) => ({ ...t, textColor: e.target.value }))
                  }
                  className="border rounded px-2 py-1 text-xs w-24"
                />
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Footer Text
            </label>
            <input
              type="text"
              value={theme.footerText ?? ""}
              placeholder="© 2026 <<company_name>>. All rights reserved."
              onChange={(e) =>
                setTheme((t) => ({
                  ...t,
                  footerText: e.target.value || undefined
                }))
              }
              className="w-full border rounded px-3 py-2 text-sm"
            />
          </div>

          {/* Powered by Velaro */}
          <div className="border rounded-lg p-4 bg-gray-50">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={theme.poweredByEnabled}
                onChange={(e) =>
                  setTheme((t) => ({ ...t, poweredByEnabled: e.target.checked }))
                }
                className="w-4 h-4 rounded"
              />
              <div>
                <div className="text-sm font-medium text-gray-800">
                  Show &quot;Powered by Velaro&quot;
                </div>
                <div className="text-xs text-gray-500">
                  Displays the Velaro branding in the email footer. When
                  unchecked, the footer section is hidden but the layout stays
                  intact.
                </div>
              </div>
            </label>
          </div>

          {/* Live preview */}
          <div>
            <div className="text-sm font-medium text-gray-700 mb-2">
              Preview
            </div>
            <div
              style={{
                backgroundColor: theme.backgroundColor,
                color: theme.textColor,
                fontFamily: "sans-serif"
              }}
              className="rounded-lg overflow-hidden border text-sm"
            >
              {/* Header */}
              <div
                style={{
                  backgroundColor: theme.primaryColor,
                  padding: "16px 24px"
                }}
              >
                {theme.logoUrl ? (
                  <img
                    src={theme.logoUrl}
                    alt="Logo"
                    style={{ height: 32, objectFit: "contain" }}
                  />
                ) : (
                  <span
                    style={{
                      color: "#fff",
                      fontWeight: 600,
                      fontSize: 14
                    }}
                  >
                    {theme.fromName || "Your Company"}
                  </span>
                )}
              </div>
              {/* Body */}
              <div style={{ padding: "20px 24px" }}>
                <p style={{ margin: 0, fontSize: 13 }}>
                  Thank you for chatting with us! Here&apos;s a copy of your
                  conversation.
                </p>
                <div
                  style={{
                    margin: "16px 0",
                    padding: "12px",
                    background: "#fff",
                    borderRadius: 6,
                    border: "1px solid #e5e7eb"
                  }}
                >
                  <div
                    style={{
                      fontSize: 12,
                      color: "#6b7280",
                      marginBottom: 4
                    }}
                  >
                    Today, 2:14 PM
                  </div>
                  <div>
                    <strong>You</strong> — Hi, I need help with my order.
                  </div>
                  <div style={{ marginTop: 8 }}>
                    <strong style={{ color: theme.primaryColor }}>Agent</strong>{" "}
                    — Sure! What&apos;s your order number?
                  </div>
                </div>
              </div>
              {/* Footer */}
              <div
                style={{
                  padding: "12px 24px",
                  borderTop: "1px solid #e5e7eb",
                  fontSize: 11,
                  color: "#9ca3af"
                }}
              >
                <div>
                  {theme.footerText ||
                    `© 2026 ${theme.fromName || "Your Company"}. All rights reserved.`}
                </div>
                {/* Always in DOM — visibility only */}
                <div
                  style={{
                    marginTop: 4,
                    display: theme.poweredByEnabled ? "block" : "none"
                  }}
                >
                  Powered by{" "}
                  <span style={{ color: theme.primaryColor }}>Velaro</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
